import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/App";

const LINKS = [
    { label: "About", href: "#about" },
    { label: "Snacks", href: "#snacks" },
    { label: "How it works", href: "#how-it-works" },
    { label: "Partners", href: "#partners" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    return (
        <motion.header
            data-testid="main-sticky-navigation"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                scrolled
                    ? "bg-[#F9F8F3]/85 backdrop-blur-md border-b border-[#1B4D1B]/10 shadow-[0_8px_30px_rgba(14,30,18,0.06)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
                <button
                    data-testid="nav-logo-btn"
                    onClick={() => window.__lenis?.scrollTo(0)}
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="/assets/logo.jpg"
                        alt="HealthLogic Vending logo"
                        className="h-10 w-10 object-cover rounded-full ring-1 ring-[#1B4D1B]/15 transition-transform duration-500 group-hover:rotate-[15deg]"
                    />
                    <span className="font-display font-800 tracking-tight text-lg font-extrabold text-[#0E1E12]">
                        HealthLogic<span className="text-[#3FAE4A]">.</span>
                        <span className="hidden sm:inline text-[#4A584C] font-medium text-sm ml-2 tracking-normal">
                            Vending
                        </span>
                    </span>
                </button>

                <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => go(l.href)}
                            className="relative text-sm font-medium text-[#4A584C] hover:text-[#0E1E12] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-[#F58220] after:transition-[width] after:duration-300 hover:after:w-full"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="nav-phone-btn"
                        href="tel:+15712811784"
                        className="hidden lg:flex items-center gap-2 text-sm font-semibold text-[#0E1E12] px-4 py-2 rounded-full border border-[#1B4D1B]/15 hover:border-[#3FAE4A]/50 hover:bg-white transition-colors duration-300"
                    >
                        <Phone className="h-4 w-4 text-[#3FAE4A]" />
                        571-281-1784
                    </a>
                    <button
                        data-testid="nav-request-free-machine-btn"
                        onClick={() => go("#contact")}
                        className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white bg-[#F58220] hover:bg-[#E06D0C] px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-[0_0_0_6px_rgba(245,130,32,0.15)]"
                    >
                        Free machine
                        <ArrowUpRight className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen(!open)}
                        className="md:hidden p-2 text-[#0E1E12]"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden bg-[#F9F8F3]/95 backdrop-blur-md border-b border-[#1B4D1B]/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.href}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => go(l.href)}
                                    className="text-left font-display text-2xl font-bold text-[#0E1E12]"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a href="tel:+15712811784" data-testid="nav-mobile-phone-link" className="flex items-center gap-2 text-[#3FAE4A] font-semibold">
                                <Phone className="h-4 w-4" /> 571-281-1784
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
